import { useEffect, useMemo, useState } from 'react';
import type { ViewKey } from '@dash/shared';
import { useWebSocket } from './hooks/useWebSocket.ts';
import { useTvShortcuts } from './hooks/useFullscreen.ts';
import { useRotator } from './hooks/useRotator.ts';
import { OfflineBanner } from './components/OfflineBanner.tsx';
import { Ticker } from './components/Ticker.tsx';
import { GeralView } from './views/Geral.tsx';
import { RedeView } from './views/Rede.tsx';
import { ComercialView } from './views/Comercial.tsx';
import { AdminMetasView } from './views/AdminMetas.tsx';
import { fmtAge } from './lib/format.ts';

type AppView = ViewKey | 'admin-metas';

function parseQuery() {
  const params = new URLSearchParams(window.location.search);
  const view = (params.get('view') ?? 'geral') as AppView;
  const token = params.get('token') ?? undefined;
  const kiosk = params.get('kiosk') === '1';
  const rotateSec = Number(params.get('rotate') ?? '0') || 0;
  return { view, token, kiosk, rotateSec };
}

export function App() {
  const initial = useMemo(parseQuery, []);
  if (initial.view === 'admin-metas') return <AdminShell token={initial.token} />;
  return <DashboardShell initial={initial} />;
}

function DashboardShell({ initial }: { initial: ReturnType<typeof parseQuery> }) {
  const { view: initialView, token, kiosk, rotateSec } = initial;
  const [manualView, setManualView] = useState<ViewKey>(initialView as ViewKey);
  const rotator = useRotator(['geral', 'rede', 'comercial'], rotateSec, manualView);
  const view = rotateSec > 0 ? rotator.current : manualView;

  useTvShortcuts(setManualView);

  const { snapshot, state, lastUpdate, alert } = useWebSocket(view, token);

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const ageSec = lastUpdate ? Math.round((now - lastUpdate.getTime()) / 1000) : null;

  const status = snapshot?.status ?? 'ok';
  const statusColor =
    status === 'critico' ? 'bg-[--color-crit]' : status === 'atencao' ? 'bg-[--color-warn]' : 'bg-[--color-ok]';
  const statusLabel =
    status === 'critico' ? 'crítico' : status === 'atencao' ? 'atenção' : 'operacional';

  const viewLabel: Record<ViewKey, string> = { geral: 'visão geral', rede: 'rede', comercial: 'comercial' };

  return (
    <div className="min-h-full text-[--color-fg]">
      <OfflineBanner state={state} />

      {alert && (
        <div className={`border-b border-[--color-border] px-6 py-2.5 text-sm font-medium backdrop-blur ${
          alert.severity === 'critical' ? 'bg-[--color-crit-soft]/90 text-[--color-crit]' :
          alert.severity === 'warning' ? 'bg-[--color-warn-soft]/90 text-[--color-warn]' :
          'bg-[--color-bg-soft]/90 text-[--color-info]'
        }`}>
          <span className="mr-3 inline-block rounded bg-current/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em]">
            {alert.severity}
          </span>
          {alert.text}
        </div>
      )}

      <header className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-[--color-border] bg-[--color-bg-soft]/80 px-6 py-3 backdrop-blur-md">
        <div className="flex items-center gap-4">
          <div className="relative flex items-center gap-3">
            <span className={`relative inline-block h-2.5 w-2.5 rounded-full ${statusColor} ${status === 'critico' ? 'animate-pulse-ring' : 'animate-pulse-soft'}`} />
            <h1 className="text-lg font-bold tracking-tight">
              <span className="text-[--color-fg]">Ibiunet</span>
              <span className="text-[--color-accent]">Hoje</span>
            </h1>
          </div>
          <span className="hidden h-4 w-px bg-[--color-border] md:inline-block" />
          <div className="hidden flex-col leading-tight md:flex">
            <span className="eyebrow !text-[9px]">{viewLabel[view as ViewKey] ?? view}</span>
            <span className={`text-[11px] font-semibold capitalize ${
              status === 'critico' ? 'text-[--color-crit]' : status === 'atencao' ? 'text-[--color-warn]' : 'text-[--color-ok]'
            }`}>{statusLabel}</span>
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs text-[--color-fg-dim]">
          {kiosk && (
            <span className="rounded bg-[--color-card-hi] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[--color-fg-dim]">
              kiosk
            </span>
          )}
          {rotateSec > 0 && (
            <span className="hidden items-center gap-2 md:flex">
              <span className="eyebrow !text-[9px]">próx.</span>
              <b className="text-[--color-fg]">{rotator.next}</b>
              <span className="relative inline-block h-1 w-20 overflow-hidden rounded-full bg-[--color-card-hi]">
                <span
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-[--color-info] to-[--color-accent]"
                  style={{ width: `${Math.round(rotator.progress * 100)}%` }}
                />
              </span>
              <span className="tabular-nums text-[--color-fg-muted]">{rotateSec}s</span>
            </span>
          )}
          <span className="tabular-nums text-[--color-fg-muted]">
            {ageSec !== null ? `· ${fmtAge(ageSec)}` : 'aguardando'}
          </span>
          <span className="rounded-md border border-[--color-border] bg-[--color-card] px-2.5 py-1 font-mono text-[13px] tabular-nums text-[--color-fg]">
            {new Date(now).toLocaleTimeString('pt-BR')}
          </span>
        </div>
      </header>

      <main className="pb-16">
        {!snapshot ? (
          <div className="flex h-[60vh] items-center justify-center text-[--color-fg-muted]">
            <span className="animate-pulse-soft">aguardando primeiro snapshot...</span>
          </div>
        ) : view === 'rede' ? (
          <RedeView snap={snapshot} />
        ) : view === 'comercial' ? (
          <ComercialView snap={snapshot} />
        ) : (
          <GeralView snap={snapshot} />
        )}
      </main>

      <Ticker items={snapshot?.avisos ?? []} />
    </div>
  );
}

function AdminShell({ token }: { token?: string }) {
  return (
    <div className="min-h-full text-[--color-fg]">
      <header className="flex items-center justify-between gap-4 border-b border-[--color-border] bg-[--color-bg-soft]/80 px-6 py-3 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-bold tracking-tight">
            <span className="text-[--color-fg]">Ibiunet</span>
            <span className="text-[--color-accent]">Hoje</span>
          </h1>
          <span className="h-4 w-px bg-[--color-border]" />
          <span className="eyebrow">admin · metas</span>
        </div>
        <a
          href="?view=geral"
          className="text-xs uppercase tracking-wider text-[--color-fg-muted] hover:text-[--color-fg]"
        >
          ← voltar p/ dashboard
        </a>
      </header>
      <main>
        <AdminMetasView token={token} />
      </main>
    </div>
  );
}
